import React, { useState, useEffect, useCallback, useRef } from 'react';
import { invoicesService, fiscalService } from '../services/api';
import { fmtDate, fmtDateTime, parseLocalDate, getTodayStr } from '../utils/formatters';
import PeriodSelector, { PeriodFilter, periodToParams } from './PeriodSelector';
import { usePeriodoNaUrl } from '../utils/periodoNaUrl';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import ClientPopover from './ClientPopover';
import { useSortState } from './shared/SortableTableHeader';
import CascaDeModal from './shared/CascaDeModal';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, Carga, EstadoDaLista, acaoPrincipal, acaoSecundaria, mensagemDoErroDeCarga, useEstadoDaCarga, campo } from '../design';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { tomDoAtraso } from '../utils/recebiveis';
import Icon from './shared/Icon';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaCliente, CelulaDinheiro, CelulaAcoes,
  CartaoDoTopo, CelulaPrazo, barraDoEstado, tomDoPrazo,
} from './shared/lista/PecasDaLista';
import type { ItemDeMenu } from './shared/MaisAcoes';

interface ReceivablesSummary {
  totals: {
    totalOpen: number;
    totalReceived: number;
    totalPaid?: number;
    totalAll?: number;
    totalOverdue: number;
    totalUpcoming: number;
    totalAntecipado?: number;
  };
  counts?: {
    open: number;
    received: number;
    paid?: number;
    all?: number;
    overdue: number;
    upcoming: number;
    antecipado?: number;
  };
  aging: {
    current: { count: number; value: number };
    days1to15: { count: number; value: number };
    days16to30: { count: number; value: number };
    days31to60: { count: number; value: number };
    days60plus: { count: number; value: number };
  };
  byPaymentMethod: Record<string, { count: number; open: number; paid: number }>;
  recentPayments: Array<{
    id: number;
    code: string;
    client: string;
    value: number;
    paymentDate: string;
    paymentMethod: string;
  }>;
}

interface OpenInvoice {
  id: number;
  code: string;
  clientId: number;
  // O backend carrega a relação `client` inteira na lista de faturas: o CNPJ vem (sub-linha C1).
  client?: { name?: string; tradeName?: string; companyName?: string; cnpj?: string };
  contractId?: number;
  totalValue: number;
  dueDate: string | null;
  status: string;
  referenceMonth: string;
  paymentMethod?: string;
  nfseStatus?: string;
  nfcomStatus?: string;
  nfseNumber?: string;
  nfcomNumber?: string;
  nossoNumero?: string;
  boletoStatus?: string;
  paidValue?: number;
  paymentDate?: string;
  jurosMulta?: number;
  discountStatus?: string;
  borderoNumber?: string;
}

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


const paymentMethodLabels: Record<string, string> = {
  boleto: 'Boleto',
  pix: 'PIX',
  ted: 'TED',
  deposito: 'Deposito',
  undefined: 'Sem definicao',
};

type BankTxFocus = { txId: number; bankAccountId: number; date: string };

// Botão de ação da tela (fantasma). O sólido fica só para o criar, que nesta tela não há (§5b).
const BOTAO = 'bg-slate-800 border border-slate-600 text-slate-200 hover:bg-slate-700';
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/**
 * O RECORTE que a tela faz, em palavras (09/10/2026). O vazio dizia "Todas as faturas foram pagas" e "Não há pagamentos
 * registrados para este mês", e a tela recorta pela COMPETÊNCIA (modo mês) ou pelo VENCIMENTO (modo período): "todas"
 * era falso sempre que outro mês tinha fatura em aberto. Os critérios não mudam aqui (R1/R2 são do Heitor): o texto só
 * diz o recorte que a tela já faz.
 */
export function rotuloDoRecorte(f: PeriodFilter): string {
  if (f.mode === 'month') {
    if (!f.months.length) return 'em todos os meses';
    return `com competência em ${f.months.map((m) => { const [a, mm] = m.split('-'); return `${MESES[Number(mm) - 1]}/${a}`; }).join(', ')}`;
  }
  if (f.startDate && f.endDate) return `com vencimento de ${fmtDate(f.startDate)} a ${fmtDate(f.endDate)}`;
  if (f.startDate) return `com vencimento a partir de ${fmtDate(f.startDate)}`;
  if (f.endDate) return `com vencimento até ${fmtDate(f.endDate)}`;
  return 'em todos os vencimentos';
}
/** O recorte do filtro "Só vencidas", dito no cabeçalho da tabela e no vazio. */
export const RECORTE_DAS_VENCIDAS = 'de qualquer competência';
/** Duas cargas lidas como uma (a busca, que junta em aberto e pagas): erro de uma é erro; a data é a mais velha. */
function juntarCargas(a: Carga, b: Carga): Carga {
  const erro = a.estado === 'erro' ? a : b.estado === 'erro' ? b : null;
  return {
    estado: erro ? 'erro' : a.estado === 'carregando' || b.estado === 'carregando' ? 'carregando' : 'pronto',
    erro: erro ? erro.erro : null,
    atualizadoEm: a.atualizadoEm && b.atualizadoEm ? (a.atualizadoEm < b.atualizadoEm ? a.atualizadoEm : b.atualizadoEm) : null,
  };
}

/**
 * `canEdit`: as escritas desta tela caem em invoices — confirm-payment,
 * cancel-discount e restore-discount são @Roles(ADMIN) em 0a0ff7d. Leitura
 * (lista, resumo, PDF de nota) fica para quem alcança o módulo.
 */
const RecebiveisTab: React.FC<{
  onNavigate?: (section: string, search?: string) => void;
  onFocusBankTx?: (focus: BankTxFocus) => void;
  canEdit?: boolean;
}> = ({ onNavigate, onFocusBankTx, canEdit = true }) => {
  // O período na URL (15/10/2026): ?mes=… / ?de=…&ate=…; F5 e link colado mantêm o mês. Ver utils/periodoNaUrl.
  const [periodFilter, setPeriodFilter] = usePeriodoNaUrl();
  const [summary, setSummary] = useState<ReceivablesSummary | null>(null);
  const [openInvoices, setOpenInvoices] = useState<OpenInvoice[]>([]);
  // ⛔ As três cargas e o modal de pagamentos são ESTADO (09/10/2026): o catch logava e zerava, e a tabela dizia
  // "Todas as faturas foram pagas" com a API fora. A lista só é trocada quando a leitura dá certo (useEstadoDaCarga).
  const cargaDoResumo = useEstadoDaCarga();
  const cargaDasAbertas = useEstadoDaCarga();
  const cargaDasPagas = useEstadoDaCarga();
  const cargaDosPagamentos = useEstadoDaCarga();
  const { rodar: rodarResumo } = cargaDoResumo;
  const { rodar: rodarAbertas } = cargaDasAbertas;
  const { rodar: rodarPagas } = cargaDasPagas;
  const { rodar: rodarPagamentos } = cargaDosPagamentos;
  // As pagas são ~1.045 linhas: só são pedidas quando alguém precisa delas (o cartão "Total Pago" ou a busca), e a partir
  // daí recarregam junto com o resto.
  const pagasPedidas = React.useRef(false);
  const [erroDosDocs, setErroDosDocs] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<number | null>(null);
  const [batchMode, setBatchMode] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'pix',
    paymentDate: getTodayStr(),
    paidValue: '',
    notes: '',
  });
  const [confirming, setConfirming] = useState(false);
  const [paymentAcknowledged, setPaymentAcknowledged] = useState(false);
  // O erro do confirmar, DENTRO do modal: a faixa do topo da aba fica atrás dele, e quem está no modal não a vê.
  const [erroDoPagamento, setErroDoPagamento] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [cardFilter, setCardFilter] = useState<'paid' | 'open' | 'overdue' | 'upcoming' | 'antecipado' | null>(null);
  /**
   * "Só vencidas" (12/10/2026, E2 da revisão aprovada pelo Heitor): TODAS as vencidas em aberto, de QUALQUER competência.
   * A tela recorta pelo mês, e uma fatura de setembro vencida só aparecia trocando o mês. Cartão e período desligam o
   * filtro; ligar ou desligar limpa a seleção, para o lote não confirmar fatura que saiu da tela.
   */
  const [soVencidas, setSoVencidas] = useState(false);
  useEffect(() => { if (cardFilter) setSoVencidas(false); }, [cardFilter]);
  /**
   * A SELEÇÃO NÃO SOBREVIVE À TROCA DE RECORTE (12/10/2026, aprovado pelo Heitor). O lote "Confirmar Pagamento (N)" manda
   * o que está marcado, e marcar faturas num mês e trocar de mês confirmava as que tinham saído da tela. Trocar de
   * cartão, de mês/período ou ligar "Só vencidas" desmarca tudo, e a tela diz quantas sumiram até a próxima marcação.
   */
  const [desmarcadas, setDesmarcadas] = useState(0);
  const selecaoAtual = useRef(selected);
  selecaoAtual.current = selected;
  useEffect(() => {
    const n = selecaoAtual.current.size;
    if (n) { setDesmarcadas(n); setSelected(new Set()); }
  }, [cardFilter, periodFilter, soVencidas]);
  const [paidInvoices, setPaidInvoices] = useState<OpenInvoice[]>([]);
  const [sort, toggleSort, setSort] = useSortState({ col: 'dueDate', dir: 'asc' });
  const [nfDetail, setNfDetail] = useState<{ type: 'NFCom' | 'NFS-e'; numbers: string[]; invoiceCode: string; invoiceId: number } | null>(null);
  const [nfDocs, setNfDocs] = useState<any[]>([]);

  // Ao abrir o modal de NF, busca os documentos fiscais da fatura pra poder
  // abrir o PDF de cada número (mapeia número → doc id → /fiscal/<tipo>/pdf/:id).
  useEffect(() => {
    if (!nfDetail) { setNfDocs([]); setErroDosDocs(null); return; }
    let cancelled = false;
    setErroDosDocs(null);
    // A falha da leitura vira estado: sem ela, "Documento fiscal não localizado" culpava a nota pela leitura que falhou.
    fiscalService.getByInvoice(nfDetail.invoiceId)
      .then((docs: any) => { if (!cancelled) setNfDocs(Array.isArray(docs) ? docs : []); })
      .catch((e: any) => { if (!cancelled) { setNfDocs([]); setErroDosDocs(mensagemDoErroDeCarga(e)); } });
    return () => { cancelled = true; };
  }, [nfDetail]);

  const findNfDoc = (type: 'NFCom' | 'NFS-e', number: string) =>
    nfDocs.find((d: any) =>
      String(d.type).toLowerCase() === (type === 'NFCom' ? 'nfcom' : 'nfse') &&
      String(d.documentNumber) === String(number),
    );

  const openNfPdf = async (type: 'NFCom' | 'NFS-e', number: string) => {
    const doc = findNfDoc(type, number);
    if (!doc && erroDosDocs) { setMessage({ type: 'error', text: `Não consegui ler os documentos fiscais desta fatura (${erroDosDocs}).` }); return; }
    if (!doc) { setMessage({ type: 'error', text: 'Documento fiscal não localizado para abrir o PDF.' }); return; }
    try {
      const res: any = type === 'NFCom'
        ? await fiscalService.downloadNfcomPdf(doc.id)
        : await fiscalService.downloadNfsePdf(doc.id);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
      else setMessage({ type: 'error', text: 'PDF indisponível para esta NF.' });
    } catch {
      setMessage({ type: 'error', text: 'Erro ao abrir o PDF da NF.' });
    }
  };
  const [paymentsModal, setPaymentsModal] = useState<OpenInvoice | null>(null);
  const [paymentsList, setPaymentsList] = useState<any[]>([]);

  const idDoModalDePagamentos = paymentsModal?.id;
  const lerPagamentos = useCallback(() => {
    if (!idDoModalDePagamentos) return;
    rodarPagamentos(async () => {
      const data = await invoicesService.getPayments(idDoModalDePagamentos);
      setPaymentsList(Array.isArray(data) ? data : []);
    });
  }, [idDoModalDePagamentos, rodarPagamentos]);
  useEffect(() => {
    setPaymentsList([]);
    lerPagamentos();
  }, [lerPagamentos]);

  // Reset sort default when switching between open/paid cards
  useEffect(() => {
    setSort(cardFilter === 'paid'
      ? { col: 'paymentDate', dir: 'desc' }
      : { col: 'dueDate', dir: 'asc' });
  }, [cardFilter, setSort]);

  const loadSummary = useCallback(() => rodarResumo(async () => {
    const params = periodToParams(periodFilter);
    setSummary(await invoicesService.getReceivablesSummary(params.month || undefined, params.startDate, params.endDate));
  }), [periodFilter, rodarResumo]);

  // Pagas sob demanda: são 1.045 linhas que só o card "Pago" exibe. Carregar
  // junto com a tela era o grosso do payload inicial do Recebíveis.
  const loadPaidInvoices = useCallback(() => {
    pagasPedidas.current = true;
    return rodarPagas(async () => {
      const data = await invoicesService.getAll({ status: 'Paga' });
      setPaidInvoices(Array.isArray(data) ? data : data.data || []);
    });
  }, [rodarPagas]);

  // A primeira vez que alguém precisa das pagas: o cartão "Total Pago" ou a BUSCA (que junta em aberto e pagas). Antes, a
  // busca só achava uma paga se alguém já tivesse aberto o cartão na sessão; e as pagas nunca recarregavam.
  useEffect(() => {
    if ((cardFilter === 'paid' || (search && !cardFilter)) && !pagasPedidas.current) loadPaidInvoices();
  }, [cardFilter, search, loadPaidInvoices]);

  // Pede ao servidor só o que a tela mostra (as em aberto); as pagas vêm sob demanda (loadPaidInvoices).
  const loadOpenInvoices = useCallback(() => rodarAbertas(async () => {
    const all = await invoicesService.getAll({ status: 'Aberta,Parcial' });
    setOpenInvoices(Array.isArray(all) ? all : all.data || []);
  }), [rodarAbertas]);

  /** Recarrega tudo o que a tela mostra, inclusive as pagas, se já foram pedidas. */
  const recarregar = useCallback(() => {
    loadSummary();
    loadOpenInvoices();
    if (pagasPedidas.current) loadPaidInvoices();
  }, [loadSummary, loadOpenInvoices, loadPaidInvoices]);

  useEffect(() => {
    loadSummary();
    loadOpenInvoices();
  }, [loadSummary, loadOpenInvoices]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);


  const getDueDateStatus = (dueDate: string | null): 'ok' | 'warning' | 'overdue' | 'none' => {
    if (!dueDate) return 'none';
    const due = parseLocalDate(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 5) return 'warning';
    return 'ok';
  };

  const getDaysOverdue = (dueDate: string | null): number => {
    if (!dueDate) return -1;
    const due = parseLocalDate(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  };

  const toggleSelect = (id: number) => {
    setDesmarcadas(0);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setDesmarcadas(0);
    if (selected.size === filteredInvoices.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredInvoices.map(i => i.id)));
    }
  };

  // Filter invoices based on search, card filter, and aging
  // When searching, look across ALL invoices (open + paid) unless a card filter is active
  // Filtro de período. Só existia o ramo 'month': no modo "Período" a LISTA
  // ignorava o filtro enquanto os CARDS consultavam o backend com
  // startDate/endDate, então escolher 01/07 a 31/07 mostrava cards de 14
  // faturas ao lado de uma tabela com todas as 157 abertas de fev a ago.
  const noRecorte = (inv: OpenInvoice) => {
    if (periodFilter.mode === 'month' && periodFilter.months.length > 0 && !periodFilter.months.includes(inv.referenceMonth)) {
      return false;
    }
    if (periodFilter.mode === 'dateRange' && (periodFilter.startDate || periodFilter.endDate)) {
      const venc = String(inv.dueDate || '').slice(0, 10);
      if (!venc) return false;
      if (periodFilter.startDate && venc < periodFilter.startDate) return false;
      if (periodFilter.endDate && venc > periodFilter.endDate) return false;
    }
    return true;
  };
  // O tom do cartão "Vencidas" é o da vencida MAIS ANTIGA do recorte (bloco E, 12/10/2026).
  const tomDasVencidas = tomDoAtraso(Math.max(0, ...openInvoices.filter(noRecorte).map((i) => getDaysOverdue(i.dueDate))));
  const vencidasDeQualquerMes = openInvoices.filter((i) => i.status !== 'Paga' && getDaysOverdue(i.dueDate) > 0);
  const baseInvoices = soVencidas ? vencidasDeQualquerMes
    : search && !cardFilter ? [...openInvoices, ...paidInvoices] : (cardFilter === 'paid' ? paidInvoices : openInvoices);
  const filteredInvoices = baseInvoices.filter(inv => {
    // Search filter
    if (search) {
      const q = search.toLowerCase();
      const clientName = [inv.client?.name, inv.client?.tradeName, inv.client?.companyName].filter(Boolean).join(' ');
      if (!inv.code.toLowerCase().includes(q) && !clientName.toLowerCase().includes(q)) {
        return false;
      }
    }
    // Com "Só vencidas", o recorte de período não vale: a lista é de qualquer competência.
    if (!soVencidas && !noRecorte(inv)) return false;
    // Card filter (skip for 'paid' — already using paidInvoices)
    if (cardFilter && cardFilter !== 'paid') {
      if (cardFilter === 'antecipado') {
        if (inv.discountStatus !== 'antecipado') return false;
      } else {
        const days = getDaysOverdue(inv.dueDate);
        switch (cardFilter) {
          case 'open': break; // all open invoices pass
          case 'overdue': if (days <= 0) return false; break;
          case 'upcoming': if (days > 0) return false; break;
        }
      }
    }
    return true;
  }).sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    let va: any;
    let vb: any;
    switch (sort.col) {
      case 'clientName':
        va = (a.client?.name || a.client?.tradeName || a.client?.companyName || '').toLowerCase();
        vb = (b.client?.name || b.client?.tradeName || b.client?.companyName || '').toLowerCase();
        return va.localeCompare(vb, 'pt-BR') * dir;
      case 'totalValue':
        va = parseFloat(String(a.totalValue)) || 0;
        vb = parseFloat(String(b.totalValue)) || 0;
        return (va - vb) * dir;
      case 'jurosMulta':
        va = parseFloat(String((a as any).jurosMulta)) || 0;
        vb = parseFloat(String((b as any).jurosMulta)) || 0;
        return (va - vb) * dir;
      case 'paidValue':
        va = parseFloat(String((a as any).paidValue || a.totalValue)) || 0;
        vb = parseFloat(String((b as any).paidValue || b.totalValue)) || 0;
        return (va - vb) * dir;
      case 'situacao': {
        const da = a.dueDate || '9999-12-31';
        const db = b.dueDate || '9999-12-31';
        return da.localeCompare(db) * dir;
      }
      case 'code':
      case 'referenceMonth':
      case 'dueDate':
      case 'paymentDate':
      case 'paymentMethod':
      default:
        va = (a as any)[sort.col] || '';
        vb = (b as any)[sort.col] || '';
        return String(va).localeCompare(String(vb)) * dir;
    }
  });

  const openPaymentModal = (invoiceId: number | null, batch: boolean) => {
    setPaymentInvoiceId(invoiceId);
    setBatchMode(batch);
    if (invoiceId && !batch) {
      const inv = openInvoices.find(i => i.id === invoiceId);
      // Duas casas no pré-preenchimento (09/10/2026): `String(1066.4)` aparecia "1066,4" no campo numérico. É só a
      // EXIBIÇÃO: o valor segue com ponto decimal e o parse do confirmar (parseFloat) não muda. Valor inválido fica vazio,
      // e não "NaN".
      const total = inv ? parseFloat(String(inv.totalValue)) : NaN;
      setPaymentForm(prev => ({
        ...prev,
        paidValue: Number.isFinite(total) ? total.toFixed(2) : '',
      }));
    } else {
      setPaymentForm(prev => ({ ...prev, paidValue: '' }));
    }
    setPaymentAcknowledged(false);
    setErroDoPagamento(null);
    setShowPaymentModal(true);
  };

  /**
   * Confirmar pagamento (09/10/2026). O backend trata o valor como ABSOLUTO: é o TOTAL pago da fatura, e ele grava só o
   * que passa do já recebido (invoices.service, confirmPayment).
   *
   * ⛔ O LOTE estava morto: o modal do lote zerava o valor e a validação exigia o valor, então todo lote acabava em
   *    "Preencha todos os campos obrigatorios" e nada gravava. Agora cada fatura vai pelo total dela (o backend grava o
   *    restante), e a que falha é NOMEADA, com o motivo.
   * ⛔ No ÚNICO, quem digitava a PARCELA de uma fatura Parcial (40 recebidos, digita 30) não gerava pagamento nenhum, e a
   *    tela dizia "confirmada com sucesso". Agora, sem nada a gravar (total ≤ já recebido), nada é enviado e o modal diz
   *    por quê: o backend grava a forma e a data na fatura mesmo sem pagamento, então não chamar é o único jeito daqui.
   */
  // A carga que a TABELA está mostrando: as pagas, as em aberto, ou as duas (a busca sem cartão junta as duas).
  const cargaDaVisao: Carga = search && !cardFilter
    ? juntarCargas(cargaDasAbertas, cargaDasPagas)
    : cardFilter === 'paid' ? cargaDasPagas : cargaDasAbertas;

  const handleConfirmPayment = async () => {
    setErroDoPagamento(null);
    if (!paymentForm.paymentMethod || !paymentForm.paymentDate || (!batchMode && !paymentForm.paidValue)) {
      setErroDoPagamento('Preencha todos os campos obrigatórios.');
      return;
    }
    const jaRecebido = (inv?: OpenInvoice) => parseFloat(String(inv?.paidValue)) || 0;
    if (!batchMode) {
      const inv = openInvoices.find(i => i.id === paymentInvoiceId);
      const total = parseFloat(paymentForm.paidValue) || 0;
      if (!(Math.round((total - jaRecebido(inv)) * 100) > 0)) {
        setErroDoPagamento(`Nada gravado: o total informado (R$ ${fmt(total)}) não passa do que já foi recebido (R$ ${fmt(jaRecebido(inv))}). Informe o TOTAL pago da fatura, somando o que já foi recebido, e não só esta parcela.`);
        return;
      }
    }

    setConfirming(true);
    try {
      const ids = batchMode ? Array.from(selected) : paymentInvoiceId ? [paymentInvoiceId] : [];
      let confirmadas = 0;
      const falhas: string[] = [];

      for (const id of ids) {
        const inv = openInvoices.find(i => i.id === id);
        const codigo = inv?.code || `#${id}`;
        const total = batchMode ? parseFloat(String(inv?.totalValue)) || 0 : parseFloat(paymentForm.paidValue);
        if (!(Math.round((total - jaRecebido(inv)) * 100) > 0)) {
          falhas.push(`${codigo}: não há valor a receber (já recebido R$ ${fmt(jaRecebido(inv))} de R$ ${fmt(total)})`);
          continue;
        }
        try {
          await invoicesService.confirmPayment(id, {
            paymentMethod: paymentForm.paymentMethod,
            paymentDate: paymentForm.paymentDate,
            paidValue: total,
            notes: paymentForm.notes || undefined,
          });
          confirmadas++;
        } catch (err: any) {
          const doServidor = err?.response?.data?.message;
          falhas.push(`${codigo}: ${(Array.isArray(doServidor) ? doServidor.join('; ') : doServidor) || mensagemDoErroDeCarga(err)}`);
        }
      }

      setShowPaymentModal(false);
      setSelected(new Set());
      setPaymentForm({
        paymentMethod: 'pix',
        paymentDate: getTodayStr(),
        paidValue: '',
        notes: '',
      });

      if (falhas.length > 0) {
        setMessage({ type: 'error', text: `${confirmadas} confirmada(s). Não confirmada(s): ${falhas.join(' · ')}` });
      } else {
        setMessage({ type: 'success', text: `${confirmadas} fatura(s) confirmada(s) com sucesso.` });
      }

      recarregar();
    } finally {
      setConfirming(false);
    }
  };

  // A ordenação de sempre (useSortState), ligada às colunas do cabeçalho da lista.
  const coluna = (c: string) => ({ ordem: sort.col === c ? sort.dir : (false as const), onOrdenar: () => toggleSort(c) });

  // A antecipação saiu da linha para o ⋯ (padrão das listas, 22/10/2026); a pergunta e o efeito são os de antes.
  const estornar = async (id: number) => {
    if (!window.confirm('Cancelar antecipação desta fatura? O recebível voltará a pendente.')) return;
    try {
      await invoicesService.cancelDiscount(id);
      setMessage({ type: 'success', text: 'Antecipação cancelada (estornado)' });
      recarregar();
    } catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Erro ao cancelar' }); }
  };
  const restaurar = async (id: number) => {
    if (!window.confirm('Restaurar antecipação desta fatura?')) return;
    try {
      await invoicesService.restoreDiscount(id);
      setMessage({ type: 'success', text: 'Antecipação restaurada' });
      recarregar();
    } catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Erro ao restaurar' }); }
  };

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? `${TONE_SURFACE.success} ${TONE_TEXT.success}` : 'bg-red-500/20 text-red-300'}`}>
          {message.text}
        </div>
      )}

      <ListToolbar
        period={
          <PeriodSelector
            value={periodFilter}
            onChange={(f) => { setPeriodFilter(f); setCardFilter(null); setSoVencidas(false); }}
            onRefresh={recarregar}
          />
        }
        search={<ToolbarSearch value={search} onChange={setSearch} placeholder="Buscar por código ou cliente..." />}
      />

          {/* Summary Cards. ⛔ Painel sem tom (09/10/2026): o número do cartão é texto e o rótulo diz o que ele é; o cartão
              escolhido é marcado por peso e borda neutra, não por laranja. A recarga que falha mantém os cartões, com a faixa. */}
          <EstadoDaLista carga={cargaDoResumo} temDados={!!summary} oQue="os totais" onTentarDeNovo={loadSummary} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {summary && (
            <>
              {/* Cartões do topo (padrão das listas, 22/10/2026): o CartaoDoTopo de Clientes. O escolhido é a barra primary; o
                  número é texto; Vencidas leva a barra no tom do atraso quando há vencida (a cor fica para o que pede atenção). */}
              <CartaoDoTopo
                className="w-full"
                rotulo="Faturamento Total"
                valor={`R$ ${fmt(summary.totals.totalAll ?? (summary.totals.totalOpen + (summary.totals.totalPaid ?? summary.totals.totalReceived)))}`}
                sub={`${summary.counts?.all ?? '-'} faturas`}
                ativo={!cardFilter}
                onClick={() => { setCardFilter(null); }}
              />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CartaoDoTopo
                  rotulo="Total Pago"
                  valor={`R$ ${fmt(summary.totals.totalPaid ?? summary.totals.totalReceived)}`}
                  sub={`${summary.counts?.paid ?? summary.counts?.received ?? 0} faturas`}
                  ativo={cardFilter === 'paid'}
                  onClick={() => { setCardFilter(cardFilter === 'paid' ? null : 'paid'); }}
                />
                <CartaoDoTopo
                  rotulo="A Receber (Abertas)"
                  valor={`R$ ${fmt(summary.totals.totalOpen)}`}
                  sub={`${summary.counts?.open ?? 0} faturas`}
                  ativo={cardFilter === 'open'}
                  onClick={() => { setCardFilter(cardFilter === 'open' ? null : 'open'); }}
                />
                <CartaoDoTopo
                  rotulo={
                    <span className="flex items-center gap-1.5">
                      {tomDasVencidas && (summary.counts?.overdue ?? 0) > 0 && <Icon name="alerta" className={`w-4 h-4 ${TONE_TEXT[tomDasVencidas]}`} />}
                      Vencidas
                    </span>
                  }
                  valor={`R$ ${fmt(summary.totals.totalOverdue)}`}
                  sub={`${summary.counts?.overdue ?? 0} faturas`}
                  ativo={cardFilter === 'overdue'}
                  barra={tomDasVencidas && (summary.counts?.overdue ?? 0) > 0 ? tomDasVencidas : null}
                  onClick={() => { setCardFilter(cardFilter === 'overdue' ? null : 'overdue'); }}
                />
                <CartaoDoTopo
                  rotulo="A Vencer"
                  valor={`R$ ${fmt(summary.totals.totalUpcoming)}`}
                  sub={`${summary.counts?.upcoming ?? 0} faturas`}
                  ativo={cardFilter === 'upcoming'}
                  onClick={() => { setCardFilter(cardFilter === 'upcoming' ? null : 'upcoming'); }}
                />
                {(summary.counts?.antecipado ?? 0) > 0 && (
                  <CartaoDoTopo
                    rotulo="Antecipado"
                    valor={`R$ ${fmt(summary.totals.totalAntecipado)}`}
                    sub={`${summary.counts?.antecipado ?? 0} faturas`}
                    ativo={cardFilter === 'antecipado'}
                    onClick={() => { setCardFilter(cardFilter === 'antecipado' ? null : 'antecipado'); }}
                  />
                )}
              </div>


            </>
          )}
          </EstadoDaLista>

          {/* Paid summary bar */}
          {cardFilter === 'paid' && filteredInvoices.length > 0 && (
            <div className="bg-central-cartao rounded-central border border-central-borda p-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-slate-300 text-sm">
                {filteredInvoices.length} fatura(s) — <span className="text-central-texto font-semibold">R$ {fmt(filteredInvoices.reduce((s, inv) => s + (parseFloat(String(inv.paidValue || inv.totalValue)) || 0), 0))}</span> recebido
                {(() => {
                  const jm = filteredInvoices.reduce((s, inv) => s + (parseFloat(String(inv.jurosMulta)) || 0), 0);
                  return jm > 0 ? <> — Juros/Multa: <span className="text-amber-400 font-semibold">R$ {fmt(jm)}</span></> : null;
                })()}
              </span>
            </div>
          )}

          {/* Invoices Table */}
          {/* A casca da lista, SEM overflow: com ele o cabeçalho não fixava (guia §3.6). */}
          <CascaDaLista>
            <div className="p-4 border-b border-central-borda flex items-center justify-between">
              <h3 className="text-white font-semibold">
                {search && !cardFilter ? 'Resultados da Busca' : cardFilter === 'paid' ? 'Faturas Pagas' : 'Faturas em Aberto'}
                {/* O "(N)" só com a lista lida: com a API fora ele dizia "(0)", um zero que ninguém contou. */}
                {cargaDaVisao.atualizadoEm && <span className="text-slate-500 text-sm font-normal ml-2">({filteredInvoices.length})</span>}
                <span className="block text-slate-500 text-xs font-normal mt-0.5" data-recorte>
                  {soVencidas ? RECORTE_DAS_VENCIDAS : rotuloDoRecorte(periodFilter)}
                  {(cardFilter === 'paid' || (search && !cardFilter)) && cargaDasPagas.atualizadoEm ? ` · pagas lidas em ${fmtDateTime(cargaDasPagas.atualizadoEm)}` : ''}
                </span>
              </h3>
              <div className="flex items-center gap-3">
                {desmarcadas > 0 && selected.size === 0 && (
                  <span role="status" className="text-xs text-slate-400">
                    {desmarcadas === 1 ? '1 fatura desmarcada' : `${desmarcadas} faturas desmarcadas`} ao trocar o filtro
                  </span>
                )}
                {cardFilter !== 'paid' && (
                  <button
                    type="button"
                    aria-pressed={soVencidas}
                    onClick={() => { setSoVencidas((v) => !v); setCardFilter(null); }}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${BOTAO} ${soVencidas ? 'font-semibold border-slate-300 text-white' : ''}`}
                    title="Todas as faturas vencidas em aberto, de qualquer competência (ignora o mês da tela)"
                  >
                    {/* O "(N)" só com a lista lida, como o contador da tabela. */}
                    Só vencidas{cargaDasAbertas.atualizadoEm ? ` (${vencidasDeQualquerMes.length})` : ''}
                  </button>
                )}
                {filteredInvoices.length > 0 && (
                  <button
                    onClick={() => {
                      const isPaid = cardFilter === 'paid';
                      const header = isPaid
                        ? ['Código', 'Cliente', 'Ref', 'Valor', 'Juros/Multa', 'Método', 'Data Pgto']
                        : ['Código', 'Cliente', 'Valor', 'Vencimento', 'Dias Vencido', 'Método'];
                      const rows = filteredInvoices.map((inv: any) => {
                        const csvEscape = (v: any) => { const s = String(v); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
                        if (isPaid) {
                          return [inv.code, inv.client?.name || inv.client?.companyName || '', inv.referenceMonth || '', inv.paidValue || inv.totalValue || 0, inv.jurosMulta || 0, inv.paymentMethod || '', inv.paymentDate?.split('T')[0] || ''].map(csvEscape).join(',');
                        }
                        // getDaysOverdue, o mesmo do badge da tabela. Antes fazia
                        // `new Date("YYYY-MM-DD")` (meia-noite UTC) contra `new Date()`
                        // (agora), então depois das 21h BRT o CSV dizia 1 dia a mais
                        // que a tela para a mesma fatura.
                        // Math.max(0, ...) mantido: getDaysOverdue devolve -1 sem
                        // vencimento e negativo para vencimento futuro.
                        const daysOver = Math.max(0, getDaysOverdue(inv.dueDate));
                        return [inv.code, inv.client?.name || inv.client?.companyName || '', inv.totalValue || 0, inv.dueDate?.split('T')[0] || '', daysOver, inv.paymentMethod || ''].map(csvEscape).join(',');
                      });
                      const csv = [header.join(','), ...rows].join('\n');
                      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = `recebiveis_${isPaid ? 'pagas' : 'aberto'}_${getTodayStr()}.csv`;
                      link.click();
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${BOTAO}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    CSV
                  </button>
                )}
                {cardFilter && (
                  <button
                    onClick={() => { setCardFilter(null); }}
                    className={`px-3 py-1.5 rounded-lg text-sm ${BOTAO}`}
                  >
                    Limpar filtro
                  </button>
                )}
                {canEdit && selected.size > 0 && cardFilter !== 'paid' && (
                  <button
                    onClick={() => openPaymentModal(null, true)}
                    className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${acaoPrincipal}`}
                  >
                    Confirmar Pagamento ({selected.size})
                  </button>
                )}
              </div>
            </div>
            <EstadoDaLista
              carga={cargaDaVisao}
              temDados={baseInvoices.length > 0}
              oQue={search && !cardFilter ? 'as faturas' : cardFilter === 'paid' ? 'as faturas pagas' : 'as faturas em aberto'}
              onTentarDeNovo={recarregar}
              vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}
            >
              <TabelaDaLista>
                <CabecalhoDaLista>
                    {cardFilter !== 'paid' && (
                      <ColunaDaLista
                        largura="w-12"
                        rotulo={filteredInvoices.length > 0 ? (
                          <input
                            type="checkbox"
                            aria-label="Selecionar todas"
                            checked={selected.size === filteredInvoices.length && filteredInvoices.length > 0}
                            onChange={toggleAll}
                            className="rounded bg-slate-700 border-slate-600"
                          />
                        ) : null}
                      />
                    )}
                    <ColunaDaLista rotulo="Código" largura="w-40" {...coluna('code')} />
                    <ColunaDaLista rotulo="Cliente" {...coluna('clientName')} />
                    <ColunaDaLista rotulo="Ref." largura="w-24" {...coluna('referenceMonth')} />
                    <ColunaDaLista rotulo="Valor" largura="w-32" alinhamento="direita" {...coluna('totalValue')} />
                    {cardFilter === 'paid' && (
                      <ColunaDaLista rotulo="Juros/Multa" largura="w-36" alinhamento="direita" {...coluna('jurosMulta')} />
                    )}
                    <ColunaDaLista rotulo="NF" largura="w-28" alinhamento="centro" />
                    {cardFilter !== 'paid' && <ColunaDaLista rotulo="Boleto" largura="w-24" alinhamento="centro" />}
                    {cardFilter === 'paid' ? (
                      <>
                        <ColunaDaLista rotulo="Método" largura="w-24" alinhamento="centro" {...coluna('paymentMethod')} />
                        <ColunaDaLista rotulo="Data Pgto." largura="w-32" {...coluna('paymentDate')} />
                      </>
                    ) : (
                      <>
                        <ColunaDaLista rotulo="Vencimento" largura="w-36" {...coluna('dueDate')} />
                        <ColunaDaLista rotulo="Situação" largura="w-40" {...coluna('situacao')} />
                        <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-36" />
                      </>
                    )}
                </CabecalhoDaLista>
                <tbody className="divide-y divide-slate-800">
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={(cardFilter === 'paid' && !search) ? 8 : 10} className="py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="p-4 bg-slate-700/50 rounded-full mb-4">
                            {cardFilter === 'paid' && !search ? (
                              <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : (
                              <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                            )}
                          </div>
                          <p className="text-slate-400 font-medium">
                            {soVencidas
                              ? (search ? `Nenhuma fatura vencida encontrada para "${search}", ${RECORTE_DAS_VENCIDAS}` : `Nenhuma fatura vencida, ${RECORTE_DAS_VENCIDAS}`)
                              : search
                              ? `Nenhuma fatura encontrada para "${search}" ${rotuloDoRecorte(periodFilter)}`
                              : `${cardFilter === 'paid' ? 'Nenhuma fatura paga' : 'Nenhuma fatura em aberto'} ${rotuloDoRecorte(periodFilter)}`}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map(inv => {
                      const dueSt = getDueDateStatus(inv.dueDate);
                      const daysOver = getDaysOverdue(inv.dueDate);
                      const isPaidView = cardFilter === 'paid' && !search;
                      // O tom do prazo vem da PEÇA (tomDoPrazo): a barra da linha e a Situação usam a mesma conta.
                      const tom = tomDoPrazo(inv.dueDate, inv.status === 'Paga');
                      // Fatura Parcial entra pelo SALDO, igual ao card "A Receber" (o backend faz `totalValue − paidValue`). A
                      // coluna mostrava o valor CHEIO, então somar a coluna dava mais que o card: com as 3 parciais de hoje,
                      // R$ 3.058,40 a mais.
                      const saldo = Math.max(Number(inv.totalValue || 0) - Number(inv.paidValue || 0), 0);
                      const parcial = Number(inv.paidValue || 0) > 0 && saldo > 0;
                      // D1: o verbo da tela (Confirmar) à vista; antecipação (estornar/restaurar) no ⋯.
                      const itens: ItemDeMenu[] = [
                        ...(canEdit && inv.discountStatus === 'antecipado' ? [{ label: 'Estornar antecipação', title: 'Cancelar antecipação (estorno)', onClick: () => estornar(inv.id) }] : []),
                        ...(canEdit && inv.discountStatus === 'estornado' ? [{ label: 'Restaurar antecipação', title: 'Restaurar antecipação', onClick: () => restaurar(inv.id) }] : []),
                      ];
                      return (
                        // Linha sem tingimento por vencimento (09/10/2026): vermelho em toda linha vencida é papel de parede.
                        <LinhaDaLista key={inv.id}>
                          {!isPaidView && (
                            // A barra lateral do atraso (bloco E): âmbar até 7 dias, vermelho depois; a linha não pinta.
                            <td className={`${CELULA} ${barraDoEstado(tom)}`} data-atraso={tom || undefined}>
                              <input
                                type="checkbox"
                                checked={selected.has(inv.id)}
                                onChange={() => toggleSelect(inv.id)}
                                className="rounded bg-slate-700 border-slate-600"
                              />
                            </td>
                          )}
                          <td className={`${CELULA} whitespace-nowrap`}>
                            {onNavigate ? (
                              <button
                                onClick={() => {
                                  const clientName = inv.client?.name || inv.client?.companyName || '';
                                  onNavigate('invoices', clientName);
                                }}
                                className="text-white font-mono hover:text-blue-400 hover:underline transition-colors cursor-pointer"
                              >
                                {inv.code}
                              </button>
                            ) : (
                              <span className="text-white font-mono">{inv.code}</span>
                            )}
                            {inv.discountStatus === 'antecipado' && (
                              <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/20 text-orange-400" title={inv.borderoNumber ? `Borderô ${inv.borderoNumber}` : 'Antecipado via borderô'}>ANT</span>
                            )}
                            {inv.discountStatus === 'estornado' && (
                              <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400" title="Antecipação estornada">EST</span>
                            )}
                          </td>
                          {/* C1: o nome (que abre o popover do cliente) e UMA sub-linha, razão · CNPJ. */}
                          <CelulaCliente
                            nome={<ClientPopover clientId={inv.clientId}>{inv.client?.name || inv.client?.companyName || '—'}</ClientPopover>}
                            razao={inv.client?.companyName && inv.client.companyName !== (inv.client?.name || inv.client?.companyName) ? inv.client.companyName : null}
                            cnpj={inv.client?.cnpj}
                          />
                          <td className={`${CELULA} text-sm text-slate-400 whitespace-nowrap`}>{inv.referenceMonth}</td>
                          {/* E1: o valor em branco; da parcial, o saldo, com o cheio embaixo. */}
                          <CelulaDinheiro valor={isPaidView ? (inv.paidValue || inv.totalValue) : (parcial ? saldo : inv.totalValue)}>
                            {!isPaidView && parcial && <p className="text-[11px] text-slate-500">de R$ {fmt(inv.totalValue)}</p>}
                          </CelulaDinheiro>
                          {isPaidView && (
                            <td className={`${CELULA} text-right font-mono whitespace-nowrap`}>
                              {inv.jurosMulta && Number(inv.jurosMulta) > 0 ? (
                                <span className="text-amber-400">+ R$ {fmt(inv.jurosMulta)}</span>
                              ) : (
                                <span className="text-slate-500">-</span>
                              )}
                            </td>
                          )}
                          <td className={`${CELULA} text-center whitespace-nowrap`}>
                            {inv.nfseStatus || inv.nfcomStatus ? (
                              <div className="flex flex-col items-center gap-0.5">
                                {inv.nfcomStatus && (() => {
                                  const status = inv.nfcomStatus.toLowerCase();
                                  // Padrão fiscal-docs: NFCom = roxo (autorizada)
                                  const colorCls = status === 'autorizada'
                                    ? 'bg-purple-500/20 text-purple-300'
                                    : status === 'erro'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-yellow-500/20 text-yellow-400';
                                  if (status === 'autorizada' && inv.nfcomNumber) {
                                    const nums = inv.nfcomNumber.split(/[,\s]+/).filter(Boolean);
                                    const label = nums.length > 2 ? `NFCom (${nums.length})` : `NFCom ${nums.join(', ')}`;
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => setNfDetail({ type: 'NFCom', numbers: nums, invoiceCode: inv.code, invoiceId: inv.id })}
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-green-400/50 transition ${colorCls}`}
                                        title="Clique para ver detalhes"
                                      >
                                        {label}
                                      </button>
                                    );
                                  }
                                  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorCls}`}>{status === 'erro' ? 'NFCom Erro' : 'NFCom Pendente'}</span>;
                                })()}
                                {inv.nfseStatus && (() => {
                                  const status = inv.nfseStatus.toLowerCase();
                                  // Padrão fiscal-docs: NFS-e = azul (autorizada)
                                  const colorCls = status === 'autorizada'
                                    ? 'bg-blue-500/20 text-blue-300'
                                    : status === 'erro'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-yellow-500/20 text-yellow-400';
                                  if (status === 'autorizada' && inv.nfseNumber) {
                                    const nums = inv.nfseNumber.split(/[,\s]+/).filter(Boolean);
                                    const label = nums.length > 2 ? `NFS-e (${nums.length})` : `NFS-e ${nums.join(', ')}`;
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => setNfDetail({ type: 'NFS-e', numbers: nums, invoiceCode: inv.code, invoiceId: inv.id })}
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-green-400/50 transition ${colorCls}`}
                                        title="Clique para ver detalhes"
                                      >
                                        {label}
                                      </button>
                                    );
                                  }
                                  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorCls}`}>{status === 'erro' ? 'NFS-e Erro' : 'NFS-e Pendente'}</span>;
                                })()}
                              </div>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          {isPaidView ? (
                            <>
                              <td className={`${CELULA} text-center whitespace-nowrap`}>
                                <button
                                  type="button"
                                  onClick={() => setPaymentsModal(inv)}
                                  className="px-2 py-0.5 rounded text-xs bg-slate-600 hover:bg-slate-500 text-slate-300 hover:text-white transition cursor-pointer"
                                  title="Ver pagamentos vinculados"
                                >
                                  {paymentMethodLabels[inv.paymentMethod || ''] || inv.paymentMethod || '-'}
                                </button>
                              </td>
                              <td className={`${CELULA} whitespace-nowrap`}>
                                <button
                                  type="button"
                                  onClick={() => setPaymentsModal(inv)}
                                  className="text-slate-300 hover:text-blue-400 hover:underline transition cursor-pointer"
                                  title="Ver pagamentos vinculados"
                                >
                                  {fmtDate(inv.paymentDate)}
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`${CELULA} text-center whitespace-nowrap`}>
                                {inv.nossoNumero ? (
                                  // E1: "Registrado" é o normal, neutro; pago e baixado pelo mapa de tons.
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    inv.boletoStatus === 'pago'
                                      ? TONE_BADGE.success
                                      : inv.boletoStatus === 'baixado'
                                        ? TONE_BADGE.danger
                                        : TONE_BADGE.neutral
                                  }`}>
                                    {inv.boletoStatus === 'pago' ? 'Pago' : inv.boletoStatus === 'baixado' ? 'Baixado' : 'Registrado'}
                                  </span>
                                ) : (
                                  <span className="text-slate-500">-</span>
                                )}
                              </td>
                              <td className={`${CELULA} whitespace-nowrap`}>
                                <div className="flex flex-col items-start leading-tight">
                                  {/* Fatura paga NÃO é "vencida": vencimento em cinza + data de pagamento */}
                                  <span className={inv.status === 'Paga' ? 'text-slate-400' : 'text-slate-200'}>
                                    {fmtDate(inv.dueDate)}
                                  </span>
                                  {inv.status === 'Paga' && inv.paymentDate && (
                                    <span className="text-[10px] text-slate-500">Pago em {fmtDate(inv.paymentDate)}</span>
                                  )}
                                </div>
                              </td>
                              {/* A Situação é a peça CelulaPrazo (22/10/2026): ícone + prazo no tom do atraso, como antes. */}
                              <CelulaPrazo
                                vencimento={inv.dueDate}
                                quitado={inv.status === 'Paga'}
                                title={!inv.dueDate
                                  ? 'Sem vencimento definido'
                                  : inv.status === 'Paga'
                                    ? `Vencimento: ${fmtDate(inv.dueDate)}${inv.paymentDate ? ` — Pago em ${fmtDate(inv.paymentDate)}` : ' — Paga'}`
                                    : dueSt === 'overdue'
                                      ? `Vencimento: ${fmtDate(inv.dueDate)} — ${daysOver} dia(s) em atraso`
                                      : dueSt === 'warning'
                                        ? `Vencimento: ${fmtDate(inv.dueDate)} — vence em ${Math.abs(daysOver)} dia(s)`
                                        : `Vencimento: ${fmtDate(inv.dueDate)} — faltam ${Math.abs(daysOver)} dia(s)`}
                              >
                                {inv.status === 'Parcial' && (
                                  <button
                                    type="button"
                                    onClick={() => setPaymentsModal(inv)}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${TONE_BADGE.neutral} hover:ring-1 hover:ring-slate-400/40 transition cursor-pointer`}
                                    title="Ver pagamentos vinculados"
                                  >
                                    Parcial (R$ {fmt(inv.paidValue || 0)} / {fmt(inv.totalValue)})
                                  </button>
                                )}
                              </CelulaPrazo>
                              <CelulaAcoes
                                rotuloDoMenu={`Mais ações da fatura ${inv.code}`}
                                verbo={canEdit ? { rotulo: 'Confirmar', onClick: () => openPaymentModal(inv.id, false), title: 'Confirmar o pagamento desta fatura' } : undefined}
                                itens={itens}
                              />
                            </>
                          )}
                        </LinhaDaLista>
                      );
                    })
                  )}
                </tbody>
              </TabelaDaLista>
            </EstadoDaLista>
          </CascaDaLista>

      {/* NF Detail Modal (list all numbers when invoice has many NFs) */}
      {nfDetail && (
        // Só leitura: fecha ao clicar fora (como já era).
        <CascaDeModal
          largura="base"
          fecharAoClicarFora
          titulo={`${nfDetail.type} da fatura ${nfDetail.invoiceCode}`}
          subtitulo={`${nfDetail.numbers.length} nota(s)`}
          onClose={() => setNfDetail(null)}
          corpoClassName="p-5"
          rodape={
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(nfDetail.numbers.join(', '));
                  setMessage({ type: 'success', text: 'Números copiados para a área de transferência' });
                }}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-200 text-sm transition-colors"
              >
                Copiar números
              </button>
              <button
                onClick={() => setNfDetail(null)}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-200 text-sm transition-colors"
              >
                Fechar
              </button>
            </div>
          }
        >
              <p className="text-xs text-slate-500 mb-2">Clique num número para abrir o PDF da nota.</p>
              <div className="flex flex-wrap gap-1.5">
                {nfDetail.numbers.map(n => {
                  const doc = findNfDoc(nfDetail.type, n);
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => openNfPdf(nfDetail.type, n)}
                      disabled={!doc}
                      title={doc ? 'Abrir PDF da NF' : 'PDF não localizado (documento não encontrado)'}
                      className={`px-2 py-1 rounded text-xs font-mono transition ${doc
                        ? `${TONE_BADGE.open} hover:brightness-125 cursor-pointer`
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'}`}
                    >
                      {nfDetail.type} {n}{doc ? ' ↗' : ''}
                    </button>
                  );
                })}
              </div>
        </CascaDeModal>
      )}

      {paymentsModal && (() => {
        const inv = paymentsModal;
        const totalPaid = paymentsList.reduce((sum, p) => sum + Number(p.amount || 0), 0);
        const sourceLabels: Record<string, string> = {
          cnab_retorno: 'CNAB Retorno',
          reconciliation: 'Conciliação bancária',
          manual: 'Manual',
          discount: 'Borderô / Antecipação',
        };
        // Origem do pagamento é categoria: tons do mapa (§4), com a borda do mesmo tom.
        const sourceColors: Record<string, string> = {
          cnab_retorno: `${TONE_BADGE.info} ${TONE_BORDER.info}`,
          reconciliation: `${TONE_BADGE.progress} ${TONE_BORDER.progress}`,
          manual: `${TONE_BADGE.neutral} ${TONE_BORDER.neutral}`,
          discount: `${TONE_BADGE.warning} ${TONE_BORDER.warning}`,
        };
        return (
          // Só leitura: fecha ao clicar fora (como já era). Total, pago e saldo na faixa.
          <CascaDeModal
            largura="base"
            fecharAoClicarFora
            titulo="Pagamentos da Fatura"
            subtitulo={`${inv.code} · ${inv.client?.name || inv.client?.companyName || ''}`}
            onClose={() => setPaymentsModal(null)}
            faixa={[
              { k: 'Total', v: `R$ ${fmt(inv.totalValue)}` },
              { k: 'Pago', v: `R$ ${fmt(totalPaid)}`, cls: TONE_TEXT.open },
              ...(inv.status === 'Parcial'
                ? [{ k: 'Saldo', v: `R$ ${fmt(Number(inv.totalValue) - totalPaid)}`, cls: TONE_TEXT.warning }]
                : []),
            ]}
            corpoClassName="p-6 space-y-3"
            rodape={
              <button onClick={() => setPaymentsModal(null)} className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors">
                Fechar
              </button>
            }
          >
                <EstadoDaLista
                  carga={cargaDosPagamentos}
                  temDados={paymentsList.length > 0}
                  oQue="os pagamentos"
                  onTentarDeNovo={lerPagamentos}
                  vazio={<div className="text-slate-500 text-sm py-8 text-center">Nenhum pagamento registrado.</div>}
                >
                {paymentsList.map((p: any) => {
                  const tx = p.bankTransaction;
                  const sourceKey = String(p.source || 'manual');
                  return (
                    <div key={p.id} className="bg-central-elevado rounded-lg p-4 border-l-2 border-green-500 space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="text-slate-400 text-xs mb-0.5">Pagamento</div>
                          <div className="flex items-center gap-2">
                            <span className="text-central-texto font-semibold">R$ {fmt(p.amount)}</span>
                            <span className="text-slate-500 text-sm">em {fmtDate(p.paymentDate)}</span>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${sourceColors[sourceKey] || sourceColors.manual}`}>
                          {sourceLabels[sourceKey] || sourceKey}
                        </span>
                      </div>
                      {p.notes && <div className="text-slate-500 text-xs">{p.notes}</div>}
                      {tx ? (
                        <div className="bg-central-fundo/50 rounded p-3 border border-central-borda/50 mt-2">
                          <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Transação Bancária</div>
                          <div className="text-slate-200 text-sm font-medium truncate">{tx.description || '(sem descrição)'}</div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                            <span>{fmtDate(tx.date)}</span>
                            <span className={tx.type === 'credit' ? 'text-slate-200' : 'text-red-400'}>{tx.type === 'credit' ? '+' : '−'} R$ {fmt(Math.abs(Number(tx.amount)))}</span>
                            {tx.bankAccount && <span>· {tx.bankAccount.bankName} {tx.bankAccount.accountNumber}</span>}
                          </div>
                          {onFocusBankTx && tx.bankAccountId && tx.date && (
                            <button
                              type="button"
                              onClick={() => {
                                onFocusBankTx({ txId: tx.id, bankAccountId: tx.bankAccountId, date: String(tx.date).split('T')[0] });
                                setPaymentsModal(null);
                              }}
                              className="mt-2 w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs font-medium border border-slate-600 transition-colors"
                            >
                              Abrir transação →
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="text-slate-500 text-xs italic">Sem transação bancária vinculada</div>
                      )}
                    </div>
                  );
                })}
                </EstadoDaLista>
          </CascaDeModal>
        );
      })()}

      {/* Payment Confirmation Modal */}
      {showPaymentModal && (
        // Formulário digitado: antes fechava ao clicar fora; agora só pelo × ou
        // Cancelar, para não perder o que foi digitado.
        <CascaDeModal
          largura="xs"
          titulo="Confirmar Pagamento"
          subtitulo={batchMode ? `${selected.size} faturas` : undefined}
          onClose={() => setShowPaymentModal(false)}
          corpoClassName="p-5"
          rodape={
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${acaoSecundaria}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={confirming || !paymentAcknowledged}
                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${acaoPrincipal}`}
              >
                {confirming ? 'Confirmando...' : 'Confirmar Pagamento'}
              </button>
            </div>
          }
        >
            <div className={`border rounded-lg p-3 mb-4 ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`}>
              <p className={`text-sm font-medium mb-1 ${TONE_TEXT.warning}`}>Atenção: conciliação pelo módulo Contas</p>
              <p className="text-slate-300 text-xs">
                A forma recomendada de confirmar pagamentos é pela conciliação bancária em <strong>Financeiro &gt; Contas</strong>,
                onde a transação do extrato é vinculada automaticamente à fatura. Confirmar aqui marca a fatura como paga
                sem vincular a uma transação bancária.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">Método de Pagamento *</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  className={`w-full ${campo} rounded-lg px-3 py-2`}
                >
                  <option value="pix">PIX</option>
                  <option value="ted">TED</option>
                  <option value="boleto">Boleto</option>
                  <option value="deposito">Deposito</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1">Data do Pagamento *</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                  className={`w-full ${campo} rounded-lg px-3 py-2`}
                />
              </div>

              {!batchMode && (
                <div>
                  <label className="block text-slate-400 text-sm mb-1">
                    {`Total pago da fatura (já recebido: R$ ${fmt(parseFloat(String(openInvoices.find(i => i.id === paymentInvoiceId)?.paidValue)) || 0)}) *`}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.paidValue}
                    onChange={e => setPaymentForm(prev => ({ ...prev, paidValue: e.target.value }))}
                    className={`w-full ${campo} rounded-lg px-3 py-2`}
                    placeholder="0.00"
                  />
                </div>
              )}

              {batchMode && (
                <p className="text-slate-400 text-sm">
                  Cada fatura é confirmada pelo total dela: o sistema grava só o que ainda falta receber de cada uma.
                </p>
              )}

              <div>
                <label className="block text-slate-400 text-sm mb-1">Observações</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  className={`w-full ${campo} rounded-lg px-3 py-2 h-20 resize-none`}
                  placeholder="Comprovante PIX, detalhes, etc."
                />
              </div>
            </div>

            {erroDoPagamento && (
              <p role="alert" className={`mt-4 px-3 py-2 rounded border text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} data-pagamento="erro">
                {erroDoPagamento}
              </p>
            )}

            <label className="flex items-start gap-2 mt-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={paymentAcknowledged}
                onChange={e => setPaymentAcknowledged(e.target.checked)}
                className="mt-0.5 accent-orange-500"
              />
              <span className="text-slate-400 text-xs">
                Estou ciente que a forma recomendada é pela conciliação em Contas e desejo confirmar o pagamento manualmente.
              </span>
            </label>
        </CascaDeModal>
      )}
    </div>
  );
};

export default RecebiveisTab;
